import clienteService from '@/services/ClienteService';
import territorioService from '@/services/TerritorioService';
import contratoRastreabilidadeService from '@/services/ContratoRastreabilidadeService';
import utilsService from '@/services/UtilsService';
import armazemService from '@/services/ArmazemService';
import localidadeService from '@/services/LocalidadeService';
import { debounce } from 'lodash';

const SHORT_TON_MULTIPLIER = 0.06;

export const fetchClientes = async ({ commit }) => {
    try {
        const response = await clienteService.getAllParaguai();
        commit('SET_CLIENTS', response.data);
    } catch (error) {
        throw error;
    }
};

export const fetchArmazens = async ({ commit }) => {
    try {
        const response = await armazemService.getAll();
        commit('SET_WAREHOUSES', response.data.content);
    } catch (error) {
        throw error;
    }
};

export const fetchLocalidades = async ({ commit }) => {
    try {
        const response = await localidadeService.findNivel1(5599);
        commit('SET_DEPARTMENTS', response.data);
    } catch (error) {
        throw error;
    }
};

export const fetchDistritosPorLocalidade = async ({ commit }, id) => {
    try {
        const response = await localidadeService.findNivel2(id);
        commit('SET_DISTRICTS', response.data);
    } catch (error) {
        throw error;
    }
};

export const fetchCultivations = async ({ commit }) => {
    try {
        const response = await utilsService.getCulturas();
        commit('SET_CULTIVATIONS', response.data);
    } catch (error) {
        throw error;
    }
};

export const fetchSafras = async ({ commit }) => {
    try {
        const response = await utilsService.getSafras();
        commit('SET_SAFRAS', response.data || []);
    } catch (error) {
        throw error;
    }
};

export const searchProperties = async ({ commit }, search) => {
    try {
        const params = {
            page: 0,
            size: 100,
            order: '&order=asc',
            search: search
        };
        const response = await territorioService.findAllSiseSomenteComPessoas(params);
        commit('SET_PROPERTIES', response.data.content);
    } catch (error) {
        throw error;
    }
};

export const fetchTraceabilityReports = debounce(async ({ state, commit }, isRegistrant) => {
    try {
        state.loading = true;
        const params = {
            page: state.filter.page ?? 0,
            size: state.filter.size ?? 10,
            status: state.filter.status ?? [],
            contract_number: state.filter.contract_number ?? '',
            client: state.filter.client ?? '',
            safra: state.filter.safra ?? '',
            cultivations: state.filter.cultivations ?? [],
            perfil: isRegistrant ? 'cadastrante' : 'aprovador'
        };
        const response = await contratoRastreabilidadeService.findAll(params);
        commit('SET_REPORT_LIST', response.data);
    } catch (error) {
        throw error;
    } finally {
        state.loading = false;
    }
}, 500);

export const createTraceabilityReport = async ({ commit }, data) => {
    try {
        const formData = new FormData();
        formData.append('status', data.status);
        formData.append('contractNumber', data.contract_data.contract_number);
        formData.append('contractVolume', data.contract_data.volume ?? 0);
        formData.append('culture', data.contract_data.cultivation_id);
        formData.append('harvestYear', data.contract_data.safra);
        if (data.contract_data.contract_date) {
            formData.append(
                'contractDate',
                new Date(data.contract_data.contract_date).toISOString().split('.')[0]
            );
        }

        formData.append(
            'receivingLocation',
            data.contract_data.delivery_location_id
        );
        formData.append(
            'receivingLocationId',
            data.contract_data.department
        );
        formData.append(
            'receivingDistrictId',
            data.contract_data.district
        );
        formData.append('storage', data.contract_data.warehouse_id);
        formData.append('warehouse', data.contract_data.warehouse_id);
        formData.append('protocolAnalisys', data.protocol_id ?? '');
        formData.append('client', data.contract_data.client_id);
        formData.append('documentId', '');

        data.properties.forEach((property, index) => {
            formData.append(`details[${index}].condition`, 'NAO_ANALISADO');
            formData.append(
                `details[${index}].resultadoDiagnostico`,
                JSON.stringify(property.eligibility || {})
            );
            formData.append(
                `details[${index}].document`,
                property.invoice_number
            );
            formData.append(
                `details[${index}].territoryId`,
                property.territory_id
            );
            formData.append(`details[${index}].safraYear`, data.contract_data.safra);
            formData.append(`details[${index}].addedVolume`, property.volume);
            formData.append(
                `details[${index}].analisysDate`,
                new Date().toISOString().split('.')[0]
            );
            // (property.invoice_attachments || []).forEach((file) => {
            //     formData.append(
            //         `details[${index}].files`,
            //         file,
            //         file.name
            //       );
            // });
            formData.append(
                `details[${index}].territoryPY.fincaName`,
                property.name
            );
            formData.append(
                `details[${index}].territoryPY.ruccode`,
                property.ruc || ''
            );
            formData.append(
                `details[${index}].territoryPY.territoryLyraCode`,
                property.territory_lyra_code || ''
            );
            formData.append(
                `details[${index}].territoryPY.producerLyraCode`,
                property.producer_lyra_code || ''
            );
            formData.append(`details[${index}].territoryPY.cosecha`, 'cosa');
            formData.append(`details[${index}].territoryPY.area`, (property.area && !isNaN(property.area)) ? property.area : 0);
            formData.append(
                `details[${index}].territoryPY.geometry`,
                JSON.stringify(property.eligibility.geometry || {})
            );
        });

        const response = await contratoRastreabilidadeService.create(formData);
        commit('SET_FORM', {...data, id: response.data});
        return response;
    } catch (error) {
        throw error;
    }
};

export const updateTraceabilityReport = async ({ commit }, data) => {
    try {
        const formData = new FormData();
        formData.append('id', data.id);
        formData.append('status', data.status);
        formData.append('contractNumber', data.contract_data.contract_number);
        formData.append('contractVolume', data.contract_data.volume ?? 0);
        formData.append('culture', data.contract_data.cultivation_id);
        formData.append('harvestYear', data.contract_data.safra);
        if (data.contract_data.contract_date) {
            formData.append(
                'contractDate',
                new Date(data.contract_data.contract_date).toISOString().split('.')[0]
            );
        }

        formData.append(
            'receivingLocation',
            data.contract_data.delivery_location_id
        );

        formData.append(
            'receivingLocationId',
            data.contract_data.department ?? ''
        );
        formData.append(
            'receivingDistrictId',
            data.contract_data.district ?? ''
        );
        formData.append('storage', data.contract_data.warehouse_id);
        formData.append('warehouse', data.contract_data.warehouse_id);
        formData.append('protocolAnalisys', data.protocol_id ?? '');
        formData.append('client', data.contract_data.client_id);
        formData.append('documentId', '');

        data.properties.forEach((property, index) => {
            if (property.id) {
                formData.append(`details[${index}].id`, property.id);
            }
            formData.append(`details[${index}].condition`, 'NAO_ANALISADO');
            formData.append(
                `details[${index}].resultadoDiagnostico`,
                JSON.stringify(property.eligibility || {})
            );
            formData.append(
                `details[${index}].document`,
                property.invoice_number
            );
            formData.append(
                `details[${index}].territoryId`,
                property.territory_id
            );
            formData.append(`details[${index}].safraYear`, data.contract_data.safra);
            formData.append(`details[${index}].addedVolume`, property.volume);
            formData.append(
                `details[${index}].analisysDate`,
                new Date().toISOString().split('.')[0]
            );
            // (property.invoice_attachments || []).forEach((file) => {
            //     formData.append(
            //       `details[${index}].files`,
            //       file,
            //       file.name
            //      );
            // });
            formData.append(
                `details[${index}].territoryPY.fincaName`,
                property.name
            );
            formData.append(
                `details[${index}].territoryPY.ruccode`,
                property.ruc || ''
            );
            formData.append(
                `details[${index}].territoryPY.territoryLyraCode`,
                property.territory_lyra_code || ''
            );
            formData.append(
                `details[${index}].territoryPY.producerLyraCode`,
                property.producer_lyra_code || ''
            );
            formData.append(
                `details[${index}].territoryPY.producerLyraName`,
                property.producer_lyra_name || ''
            );
            formData.append(`details[${index}].territoryPY.cosecha`, 'cosa');
            formData.append(`details[${index}].territoryPY.area`, (property.area && !isNaN(property.area)) ? property.area : 0);
            formData.append(
                `details[${index}].territoryPY.geometry`,
                JSON.stringify(property.eligibility.geometry || {})
            );
        });

        return await contratoRastreabilidadeService.create(formData);
    } catch (error) {
        throw error;
    }
};

export const persistDetailFiles = async ({ state, commit }, data) => {
    state.loading = true;
    let formDatas = [];
    
    try {
        data.properties.forEach((propertie) => {
            const formData = new FormData();
            
            formData.append('contractId', data.id);
            formData.append('detailId', propertie.id ? propertie.id : null);
            formData.append('ruc', propertie.ruc);
            formData.append('fincaName', propertie.name);
            formData.append('productLyraCode', propertie.producer_lyra_code);
            formData.append('contractNumber', data.contract_data.contract_number);

            const allAreFiles = propertie.invoice_attachments.every(item => item instanceof File);
            if (allAreFiles) {
                propertie.invoice_attachments.forEach((file) => {
                    formData.append(`files`, file, file.name);
                });

                formDatas.push(formData);
            }
        });
        for (const formData of formDatas) {
            await contratoRastreabilidadeService.persistDetailFiles(formData);
        }
    } catch (error) {
        throw error;
    } finally {
        state.loading = false;
    }
};

export const findAllDetailFiles = async ({state, commit }, data) => {
    state.loading = true;
    
    const { detailId } = data;
    try {
        return await contratoRastreabilidadeService.findAllDetailFiles(detailId);
    } catch (error) {
        throw error;
    } finally {
        state.loading = false;
    }
};

export const deleteAllDetailFiles = async ({ state, commit }, data) => {
    state.loading = true;

    const { detailId } = data;
    try {
        return await contratoRastreabilidadeService.deleteAllDetailFiles(detailId);
    } catch (error) {
        throw error;
    } finally {
        state.loading = false;
    }
};

export const fetchEligibility = async ({ commit }, data) => {
    try {
        const { id, document, contractDistrict } = data;
        const response = await contratoRastreabilidadeService.getEligibility(
            id,
            document,
            contractDistrict
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const validateAvailableBalance = async ({ state }, data) => {
    try {
        const { id, extraArea, form } = data;
        state.loading = true;

        return await contratoRastreabilidadeService.validateAvailableBalance(id, extraArea, form.contract_data.safra);
    } catch (error) {
        throw error;
    } finally {
        state.loading = false;
    }
};

export const fetchAvailableBalance = debounce(async ({ state, commit, dispatch }, data) => {
    const { id, extraArea, locale, form } = data;
    let pontuation = Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
    let pontuationSc = Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    });
    if (locale === 'es') {
        pontuation = Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        pontuationSc = Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        });
    }
    try {
        state.loading = true;
        form.status = 'NAO_ENVIADO';
        await dispatch('updateTraceabilityReport', form);

        let response = null;
        response = await contratoRastreabilidadeService.getAvailableBalance(id, extraArea, form.contract_data.safra);
        
        if (response?.data) {
            const balance = response.data.availableBalance;
            const ton = Math.ceil(balance * (10 ** 2)) / (10 ** 2);
            commit('SET_AVAILABLE_BALANCE', {
                territory_id: id,
                ton: pontuation.format(ton),
                sc: pontuationSc.format(Math.ceil(ton / SHORT_TON_MULTIPLIER)),
                valid: true
            });
        }
    } catch (error) {
        commit('SET_AVAILABLE_BALANCE', {
            territory_id: id,
            ton: pontuation.format(0),
            sc: pontuationSc.format(0),
            valid: true
        });
        throw error;
    } finally {
        state.loading = false;
    }
}, 500);

export const fetchSummary = async ({ commit }, isRegistrant) => {
    try {
        const perfil = isRegistrant ? 'cadastrante' : 'aprovador';
        const response = await contratoRastreabilidadeService.getSummary(perfil);
        commit('SET_SUMMARY', response.data);
    } catch (error) {
        throw error;
    }
};

export const fetchTraceabilityReport = async ({ commit, dispatch }, id) => {
    try {
        const response = await contratoRastreabilidadeService.detail(id);
        dispatch('fetchDistritosPorLocalidade', response.data.receivingLocationId);

        commit('SET_FORM', {
            id: response.data.id,
            contract_data: {
                contract_number: response.data.contractNumber,
                volume: response.data.contractVolume,
                cultivation_id: response.data.culture,
                safra: response.data.harvestYear,
                warehouse_id: response.data.storage,
                department: response.data.receivingLocationId,
                district: response.data.receivingDistrictId,
                delivery_location_id: response.data.receivingLocation,
                client_id: response.data.client,
                client_name: response.data.clientName,
                company_name: response.data.companyName,
                contract_date: response.data.contractDate
            },
            protocol_id: response.data.protocolsAnalysis,
            properties: response.data.details.map(item => ({
                id: item.id,
                area: item.territoryPY.area,
                territory_id: item.territoryId,
                invoice_number: item.document,
                volume: item.addedVolume,
                invoice_attachment: item.blobReference,
                invoice_attachments: item.blobReferences || [],
                name: item.territoryPY.fincaName,
                ruc: item.territoryPY.ruccode,
                territory_lyra_code: item.territoryPY.territoryLyraCode,
                producer_lyra_code: item.territoryPY.producerLyraCode,
                harvest: item.territoryPY.cosecha,
                eligibility: item.resultadoDiagnostico,
                analysis_date: new Date(
                    item.analisysDate
                ).toLocaleDateString()
            }))
        });
    } catch (error) {
        throw error;
    }
};

export const detail = async ({commit}, id) => {
    const response = await contratoRastreabilidadeService.detail(id);
    
    const form = {
        id: response.data.id,
        contract_data: {
            contract_number: response.data.contractNumber,
            volume: response.data.contractVolume,
            cultivation_id: response.data.culture,
            safra: response.data.harvestYear,
            warehouse_id: response.data.storage,
            department: response.data.receivingLocationId,
            district: response.data.receivingDistrictId,
            delivery_location_id: response.data.receivingLocation,
            client_id: response.data.client,
            client_name: response.data.clientName,
            company_name: response.data.companyName,
            contract_date: response.data.contractDate
        },
        protocol_id: response.data.protocolsAnalysis,
        properties: response.data.details.map(item => ({
            id: item.id,
            area: item.territoryPY.area,
            territory_id: item.territoryId,
            invoice_number: item.document,
            volume: item.addedVolume,
            invoice_attachment: item.blobReference,
            invoice_attachments: item.blobReferences || [],
            name: item.territoryPY.fincaName,
            ruc: item.territoryPY.ruccode,
            territory_lyra_code: item.territoryPY.territoryLyraCode,
            producer_lyra_code: item.territoryPY.producerLyraCode,
            harvest: item.territoryPY.cosecha,
            eligibility: item.resultadoDiagnostico,
            analysis_date: new Date(
                item.analisysDate
            ).toLocaleDateString()
        }))
    };

    return form;
};

export const viewTraceabilityReport = async ({ commit }, id) => {
    try {
        const response = await contratoRastreabilidadeService.detail(id);
        commit('SET_REPORT', {
            id: response.data.id,
            contract_number: response.data.contractNumber,
            volume: response.data.contractVolume,
            cultivation: response.data.culture,
            safra: response.data.harvestYear,
            warehouse: response.data.storage,
            delivery_location: response.data.receivingLocation,
            client: response.data.client,
            client_name: response.data.clientName,
            company_name: response.data.companyName,
            min_volume: response.data.minVolume,
            max_volume: response.data.maxVolume,
            average_volume: response.data.averageVolume,
            status: response.data.status,
            contract_date: new Date(
                response.data.contractDate
            ).toLocaleDateString(),
            protocol: response.data.protocolsAnalysis,
            properties: response.data.details.map(item => ({
                id: item.id,
                area: item.territoryPY.area,
                territory_id: item.territoryId,
                invoice_number: item.document,
                volume: item.addedVolume,
                safraYear: item.safraYear,
                invoice_attachment: item.blobReference,
                invoice_attachments: item.blobReferences || [],
                nome: item.territoryPY.fincaName,
                ruc: item.territoryPY.ruccode,
                territory_lyra_code: item.territoryPY.territoryLyraCode,
                producer_lyra_code: item.territoryPY.producerLyraCode,
                harvest: item.territoryPY.cosecha,
                eligibility: item.resultadoDiagnostico,
                analysis_date: new Date(
                    item.analisysDate
                ).toLocaleDateString()
            }))
        });
    } catch (error) {
        throw error;
    }
};

export const destroyTraceabilityReport = async ({ commit }, id) => {
    return contratoRastreabilidadeService.deleteReport(id);
};

export const downloadTraceabilityReport = async ({ commit }, id) => {
    return contratoRastreabilidadeService.downloadReport(id);
};

export const downloadInvoice = async ({ commit }, data) => {
    const { reportId, invoiceId } = data;
    return contratoRastreabilidadeService.downloadInvoice(reportId, invoiceId);
};

export const downloadInvoices = async ({ commit }, data) => {
    const { detailId } = data;
    return contratoRastreabilidadeService.downloadInvoices(detailId);
};

export const reportApprove = async ({ commit }, reportId) => {
    return contratoRastreabilidadeService.reportApprove(reportId);
};

export const reportReprove = async ({ commit }, data) => {
    const { reportId, reason, justification } = data;
    return contratoRastreabilidadeService.reportReprove(reportId, reason, justification);
};
